import type { BelegRow, BelegDatei } from './types';

export function decodeText(bytes: ArrayBuffer): string {
  let t = new TextDecoder('utf-8').decode(new Uint8Array(bytes)).replace(/^﻿/, '');
  if (/Ã[\x80-\xff]|Ã„|Ã–|Ãœ|ÃŸ|ï»¿/.test(t)) {
    try {
      const b2 = Uint8Array.from(t, (c) => c.charCodeAt(0) & 0xff);
      t = new TextDecoder('utf-8').decode(b2).replace(/^﻿/, '');
    } catch { /* Original behalten */ }
  }
  return t;
}

export function objektnummerAusDateiname(name: string): string | null {
  const vorKlammer = (name || '').split('(')[0];
  const m = vorKlammer.match(/(?:Belegungsliste|Mietvertr(?:ä|ae)ge|Eigent(?:ü|ue)mervertr(?:ä|ae)ge|Kautionen)[_ ]+(\d{3,4}(?:\.\d+)?)/i);
  if (m) return m[1];
  const f = vorKlammer.match(/(\d{3,4}(?:\.\d+)?)/);
  return f ? f[1] : null;
}

export function deDateToIso(d: string): string | null {
  const m = (d || '').trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
}

export function parseDeNumber(s: string): number | null {
  const t = (s || '').trim();
  if (t === '') return null;
  const n = parseFloat(t.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export function erkenneFormat(headerZeile: string): 'belegung' | 'mietvertraege' | 'eigentuemer' | 'kaution' | 'unbekannt' {
  const h = (headerZeile || '').toLowerCase();
  if (h.includes('kontakt name') || h.includes('kontakt e-mail')) return 'belegung';
  if (h.includes('hg-vorschuss') || h.includes('sev-status')) return 'eigentuemer';
  if (h.includes('kaution saldo') || h.includes('kaution type')) return 'kaution';
  if (h.includes('vertrag name') && h.includes('miete betrag')) return 'mietvertraege';
  return 'unbekannt';
}

export function normName(s: string): string {
  return (s || '')
    .toLowerCase()
    .replace(/\b(herr|frau|familie|fam|herrn|dr)\b\.?/g, ' ')
    .replace(/,/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const FIRMA_RE = /\b(gmbh|mbh|kg|ohg|gbr|holding|immobilien|real estate|gesellschaft|trading|e\.?\s?v)\b/i;

export function splitName(roh: string): { vorname: string; nachname: string; firma_name: string | null } {
  const name = (roh || '').replace(/,+\s*$/, '').trim();
  if (!name) return { vorname: '', nachname: 'Unbekannt', firma_name: null };
  if (FIRMA_RE.test(name)) return { vorname: '', nachname: name, firma_name: name };
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { vorname: '', nachname: name, firma_name: null };
  return { vorname: parts.slice(0, -1).join(' '), nachname: parts[parts.length - 1], firma_name: null };
}

function splitSemi(line: string): string[] {
  const out: string[] = [];
  let cur = '', q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { q = !q; continue; }
    if (ch === ';' && !q) { out.push(cur); cur = ''; continue; }
    cur += ch;
  }
  out.push(cur);
  return out;
}

export function parseBelegung(dateiname: string, text: string): BelegDatei {
  const objektnummer = objektnummerAusDateiname(dateiname);
  const lines = (text || '').split(/\r?\n/).filter((l) => l.trim() !== '');
  if (lines.length <= 1) return { dateiname, objektnummer, quelle: 'belegung', rows: [], fehler: 'Datei leer oder ohne Datenzeilen' };
  const header = splitSemi(lines[0].replace(/^﻿/, '')).map((h) => h.replace(/^"|"$/g, '').trim());
  const idx = (n: string) => header.findIndex((h) => h.toLowerCase() === n.toLowerCase());
  const iBez = idx('Verwaltungseinheit Bezeichnung');
  if (iBez < 0) return { dateiname, objektnummer, quelle: 'belegung', rows: [], fehler: 'Spalte „Verwaltungseinheit Bezeichnung" fehlt' };
  const iLage = idx('Verwaltungseinheit Lage'), iArt = idx('Verwaltungseinheit Art der VE');
  const iAb = idx('Vertrag gültig ab'), iBis = idx('Vertrag gültig bis'), iDeb = idx('Vertrag Debitor-Nr.');
  const iName = idx('Kontakt Name'), iAdr = idx('Kontakt Adresse');
  const iMailP = idx('Kontakt E-Mail Privat'), iMailA = idx('Kontakt E-Mail Arbeit'), iMailO = idx('Kontakt E-Mail öffentlich');
  const iTelA = idx('Kontakt Telefon Arbeit'), iTelP = idx('Kontakt Telefon Privat'), iMob = idx('Kontakt Telefon Mobil');
  const get = (c: string[], i: number) => (i >= 0 ? (c[i] || '').replace(/^"|"$/g, '').trim() : '');

  const rows: BelegRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const c = splitSemi(lines[i]);
    const bez = get(c, iBez);
    if (!bez) continue;
    const nameRaw = get(c, iName).replace(/,+\s*$/, '').trim();
    rows.push({
      einheitBezeichnung: bez,
      lage: get(c, iLage),
      art: get(c, iArt),
      mietbeginn: deDateToIso(get(c, iAb)),
      mietende: deDateToIso(get(c, iBis)),
      debitor: get(c, iDeb),
      name: nameRaw.toLowerCase() === 'leerstand' ? '' : nameRaw,
      adresse: get(c, iAdr),
      email: get(c, iMailP) || get(c, iMailA) || get(c, iMailO),
      telefon: get(c, iTelA) || get(c, iTelP),
      mobil: get(c, iMob),
    });
  }
  return { dateiname, objektnummer, quelle: 'belegung', rows };
}

export function parseMietvertraege(dateiname: string, text: string): BelegDatei {
  const objektnummer = objektnummerAusDateiname(dateiname);
  const lines = (text || '').split(/\r?\n/).filter((l) => l.trim() !== '');
  if (lines.length <= 1) return { dateiname, objektnummer, quelle: 'mietvertraege', rows: [], fehler: 'Datei leer oder ohne Datenzeilen' };
  const header = splitSemi(lines[0].replace(/^﻿/, '')).map((h) => h.replace(/^"|"$/g, '').trim());
  const idx = (n: string) => header.findIndex((h) => h.toLowerCase() === n.toLowerCase());
  const iBez = idx('Verwaltungseinheit Bezeichnung');
  if (iBez < 0) return { dateiname, objektnummer, quelle: 'mietvertraege', rows: [], fehler: 'Spalte „Verwaltungseinheit Bezeichnung" fehlt' };
  const iLage = idx('Verwaltungseinheit Lage'), iArt = idx('Verwaltungseinheit Art der Einheit');
  const iZi = idx('Verwaltungseinheit Anzahl Zimmer'), iFl = idx('Verwaltungseinheit Fläche');
  const iAb = idx('Vertrag gültig ab'), iBis = idx('Vertrag gültig bis'), iDeb = idx('Vertrag Debitor-Nr.');
  const iName = idx('Vertrag Name'), iKaution = idx('Vertrag Kaution Soll');
  const iMiete = idx('monatliche Zahlungen Miete Betrag');
  const iBk = idx('monatliche Zahlungen Betriebskosten-VZ Betrag');
  const iHk = idx('monatliche Zahlungen Heizkosten-VZ Betrag');
  const get = (c: string[], i: number) => (i >= 0 ? (c[i] || '').replace(/^"|"$/g, '').trim() : '');

  const rows: BelegRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const c = splitSemi(lines[i]);
    const bez = get(c, iBez);
    if (!bez) continue;
    const art = get(c, iArt);
    const flaeche = parseDeNumber(get(c, iFl));
    const istWohn = /wohn/i.test(art);
    const nameRaw = get(c, iName).replace(/,+\s*$/, '').trim();
    rows.push({
      einheitBezeichnung: bez,
      lage: get(c, iLage),
      art,
      mietbeginn: deDateToIso(get(c, iAb)),
      mietende: deDateToIso(get(c, iBis)),
      debitor: get(c, iDeb),
      name: nameRaw.toLowerCase() === 'leerstand' ? '' : nameRaw,
      adresse: '', email: '', telefon: '', mobil: '',
      wohnflaeche: istWohn ? flaeche : null,
      nutzflaeche: istWohn ? null : flaeche,
      zimmer: parseDeNumber(get(c, iZi)),
      kaltmiete: parseDeNumber(get(c, iMiete)),
      nebenkosten: parseDeNumber(get(c, iBk)),
      heizkosten: parseDeNumber(get(c, iHk)),
      kaution: parseDeNumber(get(c, iKaution)),
    });
  }
  return { dateiname, objektnummer, quelle: 'mietvertraege', rows };
}

export function parseEigentuemervertraege(dateiname: string, text: string): BelegDatei {
  const objektnummer = objektnummerAusDateiname(dateiname);
  const lines = (text || '').split(/\r?\n/).filter((l) => l.trim() !== '');
  if (lines.length <= 1) return { dateiname, objektnummer, quelle: 'eigentuemer', rows: [], fehler: 'Datei leer oder ohne Datenzeilen' };
  const header = splitSemi(lines[0].replace(/^﻿/, '')).map((h) => h.replace(/^"|"$/g, '').trim());
  const idx = (n: string) => header.findIndex((h) => h.toLowerCase() === n.toLowerCase());
  const iBez = idx('Verwaltungseinheit Bezeichnung');
  if (iBez < 0) return { dateiname, objektnummer, quelle: 'eigentuemer', rows: [], fehler: 'Spalte „Verwaltungseinheit Bezeichnung" fehlt' };
  const iLage = idx('Verwaltungseinheit Lage'), iArt = idx('Verwaltungseinheit Art der Einheit');
  const iMea = idx('Verwaltungseinheit MEA');
  const iAb = idx('Vertrag gültig ab'), iBis = idx('Vertrag gültig bis'), iDeb = idx('Vertrag Debitor-Nr.');
  const iName = idx('Vertrag Name'), iSev = idx('Vertrag SEV-Status');
  const iHg = idx('monatliche Zahlungen HG-Vorschuss'), iRl = idx('monatliche Zahlungen RL-Vorschuss');
  const get = (c: string[], i: number) => (i >= 0 ? (c[i] || '').replace(/^"|"$/g, '').trim() : '');

  const rows: BelegRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const c = splitSemi(lines[i]);
    const bez = get(c, iBez);
    if (!bez) continue;
    const nameRaw = get(c, iName).replace(/,+\s*$/, '').trim();
    const sev = get(c, iSev);
    rows.push({
      einheitBezeichnung: bez,
      lage: get(c, iLage),
      art: get(c, iArt),
      mietbeginn: deDateToIso(get(c, iAb)),
      mietende: deDateToIso(get(c, iBis)),
      debitor: get(c, iDeb),
      name: nameRaw.toLowerCase() === 'leerstand' ? '' : nameRaw,
      adresse: '', email: '', telefon: '', mobil: '',
      mea: parseDeNumber(get(c, iMea)),
      hausgeld: parseDeNumber(get(c, iHg)),
      ruecklage: parseDeNumber(get(c, iRl)),
      sevStatus: sev || undefined,
    });
  }
  return { dateiname, objektnummer, quelle: 'eigentuemer', rows };
}

export function parseKautionen(dateiname: string, text: string): BelegDatei {
  const objektnummer = objektnummerAusDateiname(dateiname);
  const lines = (text || '').split(/\r?\n/).filter((l) => l.trim() !== '');
  if (lines.length <= 1) return { dateiname, objektnummer, quelle: 'kaution', rows: [], fehler: 'Datei leer oder ohne Datenzeilen' };
  const header = splitSemi(lines[0].replace(/^﻿/, '')).map((h) => h.replace(/^"|"$/g, '').trim());
  const idx = (n: string) => header.findIndex((h) => h.toLowerCase() === n.toLowerCase());
  const iBez = idx('Verwaltungseinheit Name');
  if (iBez < 0) return { dateiname, objektnummer, quelle: 'kaution', rows: [], fehler: 'Spalte „Verwaltungseinheit Name" fehlt' };
  const iLage = idx('Verwaltungseinheit Lage'), iName = idx('Vertrag Name');
  const iGab = idx('Kaution gültig ab'), iGbis = idx('Kaution gültig bis');
  const iGegen = idx('Kaution Gegenstand'), iType = idx('Kaution Type');
  const iRaten = idx('Kaution Anzahl Raten'), iIban = idx('Kaution IBAN');
  const iSoll = idx('Kaution Soll'), iIst = idx('Kaution Ist'), iSaldo = idx('Kaution Saldo');
  const iBem = idx('Kaution Bemerkungen');
  const get = (c: string[], i: number) => (i >= 0 ? (c[i] || '').replace(/^"|"$/g, '').trim() : '');

  const rows: BelegRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const c = splitSemi(lines[i]);
    const bez = get(c, iBez);
    if (!bez) continue;
    const nameRaw = get(c, iName).replace(/,+\s*$/, '').trim();
    const raten = parseDeNumber(get(c, iRaten));
    rows.push({
      einheitBezeichnung: bez,
      lage: get(c, iLage),
      art: '',
      mietbeginn: null,
      mietende: null,
      debitor: '',
      name: nameRaw.toLowerCase() === 'leerstand' ? '' : nameRaw,
      adresse: '', email: '', telefon: '', mobil: '',
      kaution: parseDeNumber(get(c, iSoll)),
      kautionIst: parseDeNumber(get(c, iIst)),
      kautionSaldo: parseDeNumber(get(c, iSaldo)),
      kautionRaten: raten != null ? Math.round(raten) : null,
      kautionIban: get(c, iIban) || undefined,
      kautionGegenstand: get(c, iGegen) || undefined,
      kautionArt: get(c, iType) || undefined,
      kautionGueltigAb: deDateToIso(get(c, iGab)),
      kautionGueltigBis: deDateToIso(get(c, iGbis)),
      kautionBemerkungen: get(c, iBem) || undefined,
    });
  }
  return { dateiname, objektnummer, quelle: 'kaution', rows };
}

export function parseDatei(dateiname: string, text: string): BelegDatei {
  const lines = (text || '').split(/\r?\n/).filter((l) => l.trim() !== '');
  const headerZeile = lines.length ? lines[0].replace(/^﻿/, '') : '';
  const format = erkenneFormat(headerZeile);
  if (format === 'belegung') return parseBelegung(dateiname, text);
  if (format === 'eigentuemer') return parseEigentuemervertraege(dateiname, text);
  if (format === 'kaution') return parseKautionen(dateiname, text);
  if (format === 'mietvertraege') return parseMietvertraege(dateiname, text);
  return { dateiname, objektnummer: objektnummerAusDateiname(dateiname), rows: [], fehler: 'Format nicht erkannt (Belegungsliste, Mietverträge, Eigentümerverträge oder Kautionen erwartet)' };
}
